import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { storage } from 'lib/storage';
import { STORAGE_KEYS } from 'lib/constants';

const AuthContext = createContext(null);

/** Hydrate auth state from localStorage on first render. */
function loadInitialState() {
  return {
    token: storage.get(STORAGE_KEYS.AUTH_TOKEN),
    user: storage.get(STORAGE_KEYS.USER),
  };
}

/**
 * Holds the authenticated session (token + user).
 *
 * NOTE: the operational `vendorId` is NOT derived here — it is the shop profile
 * id (`vendorProfile.profile.id`) resolved by `VendorProvider` (features/vendor).
 * This context exposes `userId` only for convenience.
 *
 * Must be rendered inside <ApolloProvider> (it uses the Apollo client to clear
 * the cache on logout).
 */
export function AuthProvider({ children }) {
  const apolloClient = useApolloClient();
  const [{ token, user }, setState] = useState(loadInitialState);

  const login = useCallback(({ token: authToken, refreshToken, user: nextUser }) => {
    storage.set(STORAGE_KEYS.AUTH_TOKEN, authToken);
    if (refreshToken) storage.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    storage.set(STORAGE_KEYS.USER, nextUser);
    setState({ token: authToken, user: nextUser });
  }, []);

  const logout = useCallback(() => {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    storage.remove(STORAGE_KEYS.USER);
    storage.remove(STORAGE_KEYS.ACTIVE_SHOP);
    setState({ token: null, user: null });
    // Drop any cached data so the next session starts clean.
    apolloClient.clearStore().catch(() => {});
  }, [apolloClient]);

  // The Apollo error link broadcasts this when the session expires (401 /
  // UNAUTHENTICATED). Reacting here keeps the transport decoupled from React.
  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('quro:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('quro:unauthorized', handleUnauthorized);
  }, [logout]);

  const value = useMemo(
    () => ({
      token,
      user,
      userId: user?.id ?? null,
      isAuthenticated: Boolean(token),
      login,
      logout,
    }),
    [token, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return context;
}
